/**
 * Deterministic, math-only report statistics (#116).
 *
 * Pure functions that aggregate the heat list (`FoundryBatchRow[]`) into
 * heat-wise / daily / weekly / monthly statistical rows. NO LLM, no
 * randomness, no fabrication — every number is computed straight from the
 * source rows, so the same input always yields the same output.
 *
 * These power the Reports "Generate" flow: the UI recomputes on each
 * Generate and renders the rows in plain tables. Mirrors the yield/reject
 * math already used in `routes/twin/batches.tsx` so the numbers reconcile
 * with the Batches table the user can cross-check.
 *
 * Inputs are the same `FoundryBatchRow` shape the Batches screen reads
 * (`toBatchRow` in live mode, `FX_BATCHES` in stub mode), so this works in
 * both modes with no backend change.
 */

import { formatDay } from "@/lib/plant-days";
import type { FoundryBatchRow } from "@/lib/widgets/__foundry_fixtures";

/** Which grouping a generated report uses. */
export type ReportGranularity = "heat" | "daily" | "weekly" | "monthly";

export interface GranularityDef {
  key: ReportGranularity;
  label: string;
  /** Column header for the first (grouping) column. */
  groupHeader: string;
  description: string;
}

export const GRANULARITIES: readonly GranularityDef[] = [
  {
    key: "heat",
    label: "Heat-wise",
    groupHeader: "Heat",
    description: "One row per heat — the rawest view.",
  },
  {
    key: "daily",
    label: "Daily",
    groupHeader: "Date",
    description: "Heats rolled up by calendar day.",
  },
  {
    key: "weekly",
    label: "Weekly",
    groupHeader: "Week (ISO)",
    description: "Heats rolled up by ISO week (Mon–Sun).",
  },
  {
    key: "monthly",
    label: "Monthly",
    groupHeader: "Month",
    description: "Heats rolled up by calendar month.",
  },
] as const;

/** A single counted defect, sorted desc by count. */
export interface DefectCount {
  defect: string;
  count: number;
}

/**
 * One row of a grouped statistical report. For heat-wise reports the
 * `groupLabel` is the heat number and `heats` is 1; for the time-bucketed
 * variants it's the bucket label and `heats` is the number of heats in it.
 */
export interface ReportStatRow {
  /** Stable key for React + sort (ISO date / ISO week / month / heat no.). */
  key: string;
  /** Human-readable label rendered in the first column. */
  groupLabel: string;
  /** Number of heats contributing to this row. */
  heats: number;
  /** Sum of castings produced (castingsTotal) across the row's heats. */
  produced: number;
  /** Sum of castings accepted. */
  accepted: number;
  /** Sum of castings rejected. */
  rejected: number;
  /** Accepted / produced, as a 0–100 percentage. **null when produced === 0** —
   *  nobody counted this bucket, and "0%" would assert that they had. */
  yieldPct: number | null;
  /** Rejected / produced, as a 0–100 percentage. **null when produced === 0.** */
  rejectionPct: number | null;
  /** Heats with status === "fail". */
  failedHeats: number;
  /** Defects tallied across the row's heats, sorted desc by count. */
  topDefects: DefectCount[];
}

/**
 * One row of the shift-performance breakdown. Grouped by the heat's `shift`
 * label (raw value when set, else the shift the plant's calendar names for
 * `startedAt`, else NO_SHIFT_LABEL; never a band cut on the viewer's clock).
 * Mirrors the per-heat math so the numbers reconcile with the main report and
 * the Batches table.
 */
export interface ShiftStatRow {
  /** Stable key + label for the shift (e.g. "A", "1", or NO_SHIFT_LABEL). */
  key: string;
  /** Human-readable label (same as key; named for symmetry with other rows). */
  label: string;
  /**
   * True when the plant's calendar named this shift for heats that recorded none. False for a recorded shift, and for
   * NO_SHIFT_LABEL, where nothing was derived.
   */
  derived: boolean;
  heats: number;
  produced: number;
  accepted: number;
  rejected: number;
  /** null when produced === 0 (see `pct`). */
  yieldPct: number | null;
  /** null when produced === 0 (see `pct`). */
  rejectionPct: number | null;
  /** This shift's single most-common defect (null when none recorded). */
  topDefect: DefectCount | null;
}

/**
 * One row of the grade-mix breakdown. Grouped by the heat's `grade`. Heats
 * with a blank grade fall into an "Unspecified" bucket rather than vanishing.
 */
/** How many part rows a report shows. Parts are the only unbounded dimension, and the
 *  table renders every row it is given. Sorted scrap-first, so the tail is the parts
 *  nobody is chasing. */
export const MAX_PART_ROWS = 25;

/** A labelled produced/accepted/rejected rollup. Shared by the grade, line and part
 *  breakdowns — they differ only in what the label means and how they're ordered. */
export interface GradeStatRow {
  key: string;
  label: string;
  heats: number;
  produced: number;
  accepted: number;
  rejected: number;
  /** null when produced === 0 (see `pct`). */
  yieldPct: number | null;
  /** null when produced === 0 (see `pct`). */
  rejectionPct: number | null;
}

/**
 * One "needs attention" heat — a single heat ranked by rejection %. Carries the
 * identifying fields an operator needs to act (heat no., date, grade, shift,
 * counts, defect) without a second lookup.
 */
export interface WorstHeatRow {
  heatNumber: string;
  /** ISO startedAt (raw), or null when unparseable/missing. */
  startedAt: string | null;
  grade: string;
  /** Shift label (raw value when set, else the plant calendar's shift, else NO_SHIFT_LABEL). */
  shift: string;
  /** True when the plant's calendar named the heat's shift; false for NO_SHIFT_LABEL (see ShiftStatRow.derived). */
  shiftDerived: boolean;
  produced: number;
  accepted: number;
  rejected: number;
  /** NOT nullable, and deliberately so: only `castingsTotal > 0` heats are eligible
   *  for this ranking, so the denominator is provably positive at the one site that
   *  builds it. Widening it and then coercing with `?? 0` would reinstate exactly the
   *  fabrication GATE-SCOPE7 removed. Asserted by `statistics.test.ts`. */
  rejectionPct: number;
  status: "pass" | "fail";
  /** The heat's recorded defect type, humanised, or null. */
  defect: string | null;
}

/**
 * Defect-concentration callout: how many distinct defect types the window
 * recorded and what share of all defect-tagged heats the single top defect
 * accounts for. A quick "is one defect dominating?" read. Zeroed counts and a
 * NULL share when no defects were recorded.
 */
export interface DefectConcentration {
  /** Number of distinct defect types recorded across the window. */
  distinctTypes: number;
  /** Total defect-tagged heats (the denominator for the share). */
  totalTagged: number;
  /** The most-common defect (null when none recorded). */
  topDefect: DefectCount | null;
  /**
   * topDefect.count ÷ totalTagged, as a 0–100 percentage. **null when there is no
   * lead defect** — there is no share of a defect that does not exist.
   *
   * GATE-SCOPE7: this was `0`, and the SCREEN never showed it (the panel renders
   * "No defects recorded in this window." when `topDefect` is null) — but the PDF,
   * HTML and XLSX all printed "Lead defect —" directly beside "Lead defect share
   * 0.0%". A number stated about a thing the same table says does not exist.
   */
  topSharePct: number | null;
}

/** The full result of a Generate run — rows + a summary footer. */
/**
 * What one report row counts. A heat report counts heats and the castings recorded on each (computeReport); a lot
 * report counts the casting lots booked in each period, for a plant that books scrap per production day and part
 * (lot-report.ts). The renderers name the unit with reportUnitWords.
 */
export type ReportUnit = "heat" | "lot";

export interface ReportResult {
  granularity: ReportGranularity;
  /** Absent on a report computed before lot reports existed (a cached one): a heat report. */
  unit?: ReportUnit;
  /**
   * A heat report of a plant that books its scrap per lot: its totals are the plant's casting lots in the window
   * (withLotTotals), and heatsProduced what its heats' rows add up to (lotTotalsNote says why they differ).
   */
  lotTotals?: { lots: number; heatsProduced: number };
  rows: ReportStatRow[];
  /** Org-wide totals across every row (the table footer / KPI strip). */
  totals: {
    heats: number;
    produced: number;
    accepted: number;
    rejected: number;
    /** null when produced === 0 across the whole window (see `pct`). */
    yieldPct: number | null;
    /** null when produced === 0 across the whole window (see `pct`). */
    rejectionPct: number | null;
    failedHeats: number;
    topDefects: DefectCount[];
  };
  /**
   * Shift-performance breakdown (additive, granularity-independent — always
   * grouped by shift over the same source rows). Sorted by the recorded shifts
   * first, then the calendar's (both by label), and NO_SHIFT_LABEL last.
   */
  byShift: ShiftStatRow[];
  /**
   * What byShift's rows count, where it is not the report's unit: "lot" when the shift split is the plant's casting
   * lots booked on each shift (a plant that books its lots per shift: lot-report.ts withLotTotals). shiftCountWord
   * names it.
   */
  byShiftUnit?: ReportUnit;
  /** A heat with no recorded shift could take its shift from the plant's calendar (computeReport's shiftOf). */
  shiftCalendar?: boolean;
  /** Grade-mix breakdown (additive). Sorted desc by parts produced. */
  byGrade: GradeStatRow[];
  /** Empty when no heat carries the dimension — a plant without lines shows no line table. */
  byLine: GradeStatRow[];
  /** Top MAX_PART_ROWS by scrap. `partsTotal` is how many distinct parts exist, so the
   *  UI can say "showing 25 of 812" instead of implying these are all of them. */
  byPart: GradeStatRow[];
  partsTotal: number;
  /**
   * Up to 10 worst heats by rejection % (additive). Only heats that produced
   * castings are eligible (a 0-produced heat has no meaningful rejection %).
   */
  worstHeats: WorstHeatRow[];
  /** Defect-concentration callout (additive). */
  defectConcentration: DefectConcentration;
  /** Reporting window derived from the source rows (ISO date, inclusive). */
  windowFrom: string | null;
  windowTo: string | null;
  /** ISO timestamp the report was computed (for the rendered header). */
  generatedAt: string;
}

// ────────────────────────────────────────────────────────────────────
// Rounding — single rule so every percentage in the report is consistent.
// ────────────────────────────────────────────────────────────────────

/** Round to one decimal place. Deterministic; avoids float dust in the UI. */
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * A percentage, or `null` when the denominator does not exist.
 *
 * GATE-SCOPE7 (2026-08-03) — THIS RETURNED `0` AND THAT WAS A CLAIM, NOT A BLANK.
 * `castingsTotal = 0` is the ordinary state of a new-model / captured heat before
 * the count lands, so any bucket built only from such heats reported
 * "Yield 0.0% · Rejection 0.0%" — the most damning pair of numbers this report can
 * print, asserted as a measurement, and carried onto the PDF / XLSX / HTML
 * artefacts that leave the building and are read years later with no DB to check
 * them against. Same fabrication S2.3b removed from the batch row and GATE-SCOPE6
 * removed from the dossier and the heats CSV; this is the compute module that
 * feeds every one of the report exports, which is why it outlived both.
 *
 * NOTE THE SHAPE, because it is why the document gate was green over it: the old
 * body was a guarded EARLY RETURN (`if (whole <= 0) return 0`), not the
 * `d > 0 ? … : 0` ternary the GATE-SCOPE6 detector was written for. The detector
 * now covers both forms; see connectivity_document_test.go.
 */
function pct(part: number, whole: number): number | null {
  if (whole <= 0) return null;
  return round1((part / whole) * 100);
}

// ────────────────────────────────────────────────────────────────────
// Bucket-key derivation (deterministic: the plant day where the plant's clock
// names one, else the UTC day of started_at)
// ────────────────────────────────────────────────────────────────────

/** Two-digit zero-pad. */
function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** YYYY-MM-DD in UTC. */
function dayKey(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/**
 * ISO-8601 week key `YYYY-Www` (UTC). Weeks start Monday; week 1 contains
 * the year's first Thursday. Standard algorithm so weekly buckets line up
 * with how ops typically reckon plant weeks.
 */
function isoWeekKey(d: Date): string {
  // Copy to a UTC midnight to avoid DST/locale drift.
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  // Thursday in the current week decides the year.
  const dayNum = (date.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  const week =
    1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
  return `${date.getUTCFullYear()}-W${pad2(week)}`;
}

/** Pretty label for a day key (e.g. "24 May 2026"). */
export function dayLabel(key: string): string {
  return formatDay(key);
}

/**
 * The shift bucket of a heat that carries no recorded shift and whose shift the plant's calendar cannot name (no
 * calendar, or no readable start time). The report never invents a shift: it used to cut such a heat into
 * Morning / Afternoon / Night by the viewer's browser hour (lib/shift-calendar).
 */
export const NO_SHIFT_LABEL = "No shift recorded";

/** Pretty label for a month key (e.g. "May 2026"). */
export function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return `${LONG_MONTHS[m - 1] ?? key} ${y}`;
}

/** The months in words, the same in every browser (the 2026-09-28 walk, R6). */
const LONG_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October",
  "November", "December"];

/**
 * Bucket key + label for one row, given a granularity. Heat-wise keys on the
 * heat number (already unique); the rest key on the day of `startedAt`: the
 * plant day where `dayOf` (the plant's clock) names one, else the UTC day.
 * The window is cut on the plant day too (computeReport), so a heat poured on
 * the evening of the 31st is the 31st's and its month's, never the next UTC
 * day's. Rows with an unparseable date fall into an "unknown" bucket rather
 * than being silently dropped: an honest report shows them.
 */
function bucketOf(
  row: FoundryBatchRow,
  g: ReportGranularity,
  dayOf?: (iso: string) => string | null,
): { key: string; label: string } {
  if (g === "heat") {
    return { key: row.heatNumber, label: row.heatNumber };
  }
  const t = Date.parse(row.startedAt);
  if (Number.isNaN(t)) {
    return { key: "unknown", label: "Unknown date" };
  }
  const day = dayOf?.(row.startedAt) || dayKey(new Date(t));
  if (g === "daily") {
    return { key: day, label: dayLabel(day) };
  }
  if (g === "monthly") {
    const key = day.slice(0, 7);
    return { key, label: monthLabel(key) };
  }
  // weekly: the ISO week of that day, read at its UTC midnight (isoWeekKey reads UTC parts).
  const key = isoWeekKey(new Date(`${day}T00:00:00Z`));
  return { key, label: key };
}

// ────────────────────────────────────────────────────────────────────
// Aggregation
// ────────────────────────────────────────────────────────────────────

/** Mutable accumulator while folding rows into a bucket. */
interface Acc {
  key: string;
  label: string;
  heats: number;
  produced: number;
  accepted: number;
  rejected: number;
  failedHeats: number;
  defects: Map<string, number>;
  /** Earliest startedAt epoch seen — used to sort time buckets chronologically. */
  firstSeen: number;
}

/** Convert a defect Map to a sorted, capped list. Stable: ties break by name. */
function sortDefects(defects: Map<string, number>): DefectCount[] {
  return Array.from(defects.entries())
    .map(([defect, count]) => ({ defect, count }))
    .sort((a, b) => b.count - a.count || a.defect.localeCompare(b.defect));
}

/** Most-common defect from a Map, or null. Ties break by name (deterministic). */
function topDefectOf(defects: Map<string, number>): DefectCount | null {
  const sorted = sortDefects(defects);
  return sorted[0] ?? null;
}

/** Accumulator for a shift bucket while folding rows. */
interface ShiftAcc {
  key: string;
  derived: boolean;
  heats: number;
  produced: number;
  accepted: number;
  rejected: number;
  defects: Map<string, number>;
}

/** Accumulator for a grade bucket while folding rows. */
interface GradeAcc {
  key: string;
  heats: number;
  produced: number;
  accepted: number;
  rejected: number;
}

/**
 * Resolve a heat's shift label: the raw `shift` (trimmed) when set, else the shift the plant's calendar names for its
 * start (`shiftOf`, lib/shift-calendar), else NO_SHIFT_LABEL. Returns the label and whether it was derived: only the
 * calendar's shift is. NO_SHIFT_LABEL is not: nothing was derived for that heat, and it used to print as
 * "No shift recorded (derived)".
 */
function resolveShift(
  row: FoundryBatchRow,
  shiftOf?: (iso: string) => string | null,
): { label: string; derived: boolean } {
  const raw = row.shift?.trim();
  if (raw) return { label: raw, derived: false };
  const fromCalendar = shiftOf?.(row.startedAt);
  if (fromCalendar) return { label: fromCalendar, derived: true };
  return { label: NO_SHIFT_LABEL, derived: false };
}

/**
 * Compute a deterministic statistical report from the heat rows.
 *
 * Honest by construction: yield/reject are derived from castings counts,
 * defect tallies count only heats that recorded a defect type, and
 * `failedHeats` counts status === "fail". Nothing is invented — an empty
 * input yields an empty report (zero rows, zeroed totals).
 */
/** Shape a bucket map into sorted-ready rows. The grade, line and part breakdowns are
 *  the same rollup over a different label, so they share one projection. */
function dimensionRows(buckets: Map<string, GradeAcc>): GradeStatRow[] {
  return Array.from(buckets.values()).map((g) => ({
    key: g.key,
    label: g.key,
    heats: g.heats,
    produced: g.produced,
    accepted: g.accepted,
    rejected: g.rejected,
    yieldPct: pct(g.accepted, g.produced),
    rejectionPct: pct(g.rejected, g.produced),
  }));
}

export function computeReport(
  rows: readonly FoundryBatchRow[],
  granularity: ReportGranularity,
  dateRange?: { from?: string | null; to?: string | null },
  opts: {
    /** The shift the plant's calendar names for a heat with no recorded shift (lib/shift-calendar shiftAt). */
    shiftOf?: (iso: string) => string | null;
    /**
     * The plant day a heat's start falls on (lib/shift-calendar plantDayOf): with it, the window is the plant's
     * days, so a heat poured on 31 Aug's evening shift is August's wherever UTC midnight falls.
     */
    dayOf?: (iso: string) => string | null;
  } = {},
): ReportResult {
  // Window the data so daily / weekly / monthly reports actually differ. The
  // bounds are inclusive day boundaries (from = start of the from-day, to = end
  // of the to-day, UTC). Rows with an unparseable startedAt are kept (they fall
  // into the "unknown" bucket) so we never silently drop data.
  const fromMs = dateRange?.from ? Date.parse(`${dateRange.from}T00:00:00Z`) : NaN;
  const toMs = dateRange?.to ? Date.parse(`${dateRange.to}T23:59:59.999Z`) : NaN;
  const inWindow = (row: FoundryBatchRow): boolean => {
    if (Number.isNaN(fromMs) && Number.isNaN(toMs)) return true;
    const day = opts.dayOf?.(row.startedAt);
    if (day) return (!dateRange?.from || day >= dateRange.from) && (!dateRange?.to || day <= dateRange.to);
    const t = Date.parse(row.startedAt);
    if (Number.isNaN(t)) return true; // keep undated rows
    if (!Number.isNaN(fromMs) && t < fromMs) return false;
    if (!Number.isNaN(toMs) && t > toMs) return false;
    return true;
  };
  const windowed = rows.filter(inWindow);

  const buckets = new Map<string, Acc>();

  // Org-wide totals (a single accumulator over every row).
  const totalDefects = new Map<string, number>();
  let tHeats = 0;
  let tProduced = 0;
  let tAccepted = 0;
  let tRejected = 0;
  let tFailed = 0;
  let minStart = Number.POSITIVE_INFINITY;
  let maxStart = Number.NEGATIVE_INFINITY;

  // Additive breakdowns — computed over the SAME pass, independent of the
  // chosen granularity (they always group by shift / grade / per-heat).
  const shiftBuckets = new Map<string, ShiftAcc>();
  const gradeBuckets = new Map<string, GradeAcc>();
  // Line and part are dimensions a foundry manages by, and they reuse the grade row
  // shape exactly (a labelled produced/accepted/rejected rollup). Both are optional on
  // the row — plant-run heats carry neither, imported lots carry both — so a plant that
  // has no notion of lines simply gets an empty array rather than an "Unspecified" row.
  const lineBuckets = new Map<string, GradeAcc>();
  const partBuckets = new Map<string, GradeAcc>();
  /** Total heats that recorded a defect type (denominator for concentration). */
  let taggedHeats = 0;
  /** Eligible heats for the "worst heats" ranking (produced > 0). */
  const worstCandidates: WorstHeatRow[] = [];

  for (const row of windowed) {
    const { key, label } = bucketOf(row, granularity, opts.dayOf);
    let acc = buckets.get(key);
    if (!acc) {
      acc = {
        key,
        label,
        heats: 0,
        produced: 0,
        accepted: 0,
        rejected: 0,
        failedHeats: 0,
        defects: new Map(),
        firstSeen: Number.POSITIVE_INFINITY,
      };
      buckets.set(key, acc);
    }

    acc.heats += 1;
    acc.produced += row.castingsTotal;
    acc.accepted += row.castingsAccepted;
    acc.rejected += row.castingsRejected;
    if (row.status === "fail") acc.failedHeats += 1;
    if (row.defectType) {
      acc.defects.set(row.defectType, (acc.defects.get(row.defectType) ?? 0) + 1);
      totalDefects.set(
        row.defectType,
        (totalDefects.get(row.defectType) ?? 0) + 1,
      );
    }

    const t = Date.parse(row.startedAt);
    if (!Number.isNaN(t)) {
      if (t < acc.firstSeen) acc.firstSeen = t;
      if (t < minStart) minStart = t;
      if (t > maxStart) maxStart = t;
    }

    tHeats += 1;
    tProduced += row.castingsTotal;
    tAccepted += row.castingsAccepted;
    tRejected += row.castingsRejected;
    if (row.status === "fail") tFailed += 1;
    if (row.defectType) taggedHeats += 1;

    // ── Shift breakdown ──────────────────────────────────────────────
    const { label: shiftLabel, derived: shiftDerived } = resolveShift(row, opts.shiftOf);
    let sAcc = shiftBuckets.get(shiftLabel);
    if (!sAcc) {
      sAcc = {
        key: shiftLabel,
        derived: shiftDerived,
        heats: 0,
        produced: 0,
        accepted: 0,
        rejected: 0,
        defects: new Map(),
      };
      shiftBuckets.set(shiftLabel, sAcc);
    }
    // A bucket is "derived" only if every contributing heat was derived; a
    // single explicit-shift heat makes the label authoritative.
    if (!shiftDerived) sAcc.derived = false;
    sAcc.heats += 1;
    sAcc.produced += row.castingsTotal;
    sAcc.accepted += row.castingsAccepted;
    sAcc.rejected += row.castingsRejected;
    if (row.defectType) {
      sAcc.defects.set(row.defectType, (sAcc.defects.get(row.defectType) ?? 0) + 1);
    }

    // ── Grade breakdown ──────────────────────────────────────────────
    const gradeLabel = row.grade?.trim() || "Unspecified";
    let gAcc = gradeBuckets.get(gradeLabel);
    if (!gAcc) {
      gAcc = {
        key: gradeLabel,
        heats: 0,
        produced: 0,
        accepted: 0,
        rejected: 0,
      };
      gradeBuckets.set(gradeLabel, gAcc);
    }
    gAcc.heats += 1;
    gAcc.produced += row.castingsTotal;
    gAcc.accepted += row.castingsAccepted;
    gAcc.rejected += row.castingsRejected;

    // ── Line / part breakdowns ───────────────────────────────────────
    // Unlike grade there is no "Unspecified" bucket: a heat with no line simply
    // isn't in the line breakdown. A plant without lines should show no line table,
    // not a single row labelled Unspecified holding everything.
    for (const [value, bucket] of [
      [row.line, lineBuckets],
      [row.part, partBuckets],
    ] as const) {
      const label = value?.trim();
      if (!label) continue;
      let acc = bucket.get(label);
      if (!acc) {
        acc = { key: label, heats: 0, produced: 0, accepted: 0, rejected: 0 };
        bucket.set(label, acc);
      }
      acc.heats += 1;
      acc.produced += row.castingsTotal;
      acc.accepted += row.castingsAccepted;
      acc.rejected += row.castingsRejected;
    }

    // ── Worst-heats candidate ────────────────────────────────────────
    // Only heats that actually produced castings have a meaningful rejection
    // %; a 0-produced heat would rank as 0% and crowd out real problems.
    //
    // GATE-SCOPE7: `pct` is now nullable, and the null is TESTED here rather than
    // coerced. `?? 0` would put an unmeasured heat back at the bottom of the
    // ranking wearing a real number — the fabrication in a new place — so the row
    // is simply not a candidate. While the `castingsTotal > 0` guard above holds,
    // the second half is unreachable; it is written so that removing the guard
    // fails closed instead of silently fabricating.
    const worstRejPct = pct(row.castingsRejected, row.castingsTotal);
    if (row.castingsTotal > 0 && worstRejPct != null) {
      worstCandidates.push({
        heatNumber: row.heatNumber,
        startedAt: Number.isNaN(Date.parse(row.startedAt)) ? null : row.startedAt,
        grade: row.grade?.trim() || "—",
        shift: shiftLabel,
        shiftDerived,
        produced: row.castingsTotal,
        accepted: row.castingsAccepted,
        rejected: row.castingsRejected,
        rejectionPct: worstRejPct,
        status: row.status,
        defect: row.defectType ? row.defectType.replace(/_/g, " ") : null,
      });
    }
  }

  const accs = Array.from(buckets.values());
  // Sort: heat-wise → newest heat first (firstSeen desc), matching the Batches
  // table order; time buckets → chronological (oldest first) so the report
  // reads like a timeline. Ties / unknown dates fall back to key compare.
  accs.sort((a, b) => {
    if (granularity === "heat") {
      if (a.firstSeen !== b.firstSeen) return b.firstSeen - a.firstSeen;
      return b.key.localeCompare(a.key);
    }
    if (a.firstSeen !== b.firstSeen) return a.firstSeen - b.firstSeen;
    return a.key.localeCompare(b.key);
  });

  const rowsOut: ReportStatRow[] = accs.map((a) => ({
    key: a.key,
    groupLabel: a.label,
    heats: a.heats,
    produced: a.produced,
    accepted: a.accepted,
    rejected: a.rejected,
    yieldPct: pct(a.accepted, a.produced),
    rejectionPct: pct(a.rejected, a.produced),
    failedHeats: a.failedHeats,
    topDefects: sortDefects(a.defects),
  }));

  // ── Shift rows ─────────────────────────────────────────────────────
  // Sort: the heats no shift names last (they are not derived, so the next rule alone would not put them there), then
  // explicit-shift buckets before the calendar's, both by label.
  const byShift: ShiftStatRow[] = Array.from(shiftBuckets.values())
    .map((s) => ({
      key: s.key,
      label: s.key,
      derived: s.derived,
      heats: s.heats,
      produced: s.produced,
      accepted: s.accepted,
      rejected: s.rejected,
      yieldPct: pct(s.accepted, s.produced),
      rejectionPct: pct(s.rejected, s.produced),
      topDefect: topDefectOf(s.defects),
    }))
    .sort((a, b) => {
      if ((a.label === NO_SHIFT_LABEL) !== (b.label === NO_SHIFT_LABEL)) return a.label === NO_SHIFT_LABEL ? 1 : -1;
      if (a.derived !== b.derived) return a.derived ? 1 : -1;
      return a.label.localeCompare(b.label, undefined, { numeric: true });
    });

  // ── Grade rows ─────────────────────────────────────────────────────
  // Sort desc by parts produced (the production-mix story), ties by label.
  const byGrade: GradeStatRow[] = dimensionRows(gradeBuckets).sort(
    (a, b) => b.produced - a.produced || a.label.localeCompare(b.label),
  );

  // ── Line rows ──────────────────────────────────────────────────────
  // Sorted by label: lines are few and stable, and an operator looks for "Line 2"
  // by name, not by rank.
  const byLine: GradeStatRow[] = dimensionRows(lineBuckets).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true }),
  );

  // ── Part rows ──────────────────────────────────────────────────────
  // Sorted by scrap DESC — the actionable order, and the one a foundry already
  // thinks in ("high scrap parts"). Ties break by produced, then label.
  //
  // Capped: unlike grade/line/shift, the part count is unbounded — a fittings foundry
  // can run thousands of part numbers over 16 months, and the table renders every row
  // (hidePagination) into both the DOM and the PDF. The cap is safe precisely BECAUSE
  // the sort is scrap-first: the tail is the parts nobody is chasing. `partsTotal` lets
  // the caller say so rather than quietly truncate.
  const allParts = dimensionRows(partBuckets).sort(
    (a, b) =>
      b.rejected - a.rejected ||
      b.produced - a.produced ||
      a.label.localeCompare(b.label),
  );
  const byPart: GradeStatRow[] = allParts.slice(0, MAX_PART_ROWS);

  // ── Worst heats ────────────────────────────────────────────────────
  // Top 10 by rejection % desc; ties break by absolute rejected count (a
  // bigger absolute scrap pile is the worse problem), then heat number for
  // a fully deterministic order.
  const worstHeats: WorstHeatRow[] = [...worstCandidates]
    .sort(
      (a, b) =>
        b.rejectionPct - a.rejectionPct ||
        b.rejected - a.rejected ||
        a.heatNumber.localeCompare(b.heatNumber),
    )
    .slice(0, 10);

  // ── Defect concentration ───────────────────────────────────────────
  const sortedTotalDefects = sortDefects(totalDefects);
  const topDefect = sortedTotalDefects[0] ?? null;
  const defectConcentration: DefectConcentration = {
    distinctTypes: sortedTotalDefects.length,
    totalTagged: taggedHeats,
    topDefect,
    // No lead defect → no share. Was `: 0`, which the documents printed as
    // "Lead defect share 0.0%" one row under "Lead defect —" (GATE-SCOPE7).
    topSharePct: topDefect ? pct(topDefect.count, taggedHeats) : null,
  };

  return {
    granularity,
    rows: rowsOut,
    totals: {
      heats: tHeats,
      produced: tProduced,
      accepted: tAccepted,
      rejected: tRejected,
      yieldPct: pct(tAccepted, tProduced),
      rejectionPct: pct(tRejected, tProduced),
      failedHeats: tFailed,
      topDefects: sortedTotalDefects,
    },
    byShift,
    shiftCalendar: opts.shiftOf !== undefined,
    byGrade,
    byLine,
    byPart,
    partsTotal: allParts.length,
    worstHeats,
    defectConcentration,
    windowFrom: dateRange?.from
      ? new Date(`${dateRange.from}T00:00:00Z`).toISOString()
      : Number.isFinite(minStart)
        ? new Date(minStart).toISOString()
        : null,
    windowTo: dateRange?.to
      ? new Date(`${dateRange.to}T23:59:59.999Z`).toISOString()
      : Number.isFinite(maxStart)
        ? new Date(maxStart).toISOString()
        : null,
    generatedAt: new Date().toISOString(),
  };
}

/**
 * One bar of a defect Pareto: a defect category + its total count across the
 * report window. `{ name, count }` so it drops straight into the generic
 * `ParetoChart` widget (which sorts desc and overlays the cumulative-% line).
 */
export interface ParetoSlice {
  name: string;
  count: number;
}

/**
 * Shape a window-wide defect tally (`ReportResult.totals.topDefects`) into
 * Pareto bars. Pure + deterministic: no sorting needed here (the source is
 * already sorted desc by `sortDefects`) — we only project the fields the
 * chart wants and optionally cap to the top `n`. Underscores are humanised
 * ("sand_inclusion" → "sand inclusion") for the axis labels; the chart owns
 * presentation but the transform is trivial and keeps the widget generic.
 *
 * Returns `[]` for an empty tally so the chart can render an honest empty
 * state rather than an axis with no bars.
 */
export function toParetoBuckets(
  defects: readonly DefectCount[],
  cap = 8,
): ParetoSlice[] {
  return defects
    .slice(0, cap)
    .map((d) => ({ name: d.defect.replace(/_/g, " "), count: d.count }));
}

/**
 * One bar of the shift yield/rejection chart: a shift label + its yield % and
 * rejection %. `{ label, yieldPct, rejectionPct }` for a grouped bar chart.
 * Pure projection of `ReportResult.byShift` (already sorted) — `[]` when empty.
 */
export interface ShiftBar {
  label: string;
  /** null when the shift counted nothing — recharts draws a GAP, which is the
   *  honest render. A 0 would draw a bar at the floor and read as total scrap. */
  yieldPct: number | null;
  /** null when the shift counted nothing (see above). */
  rejectionPct: number | null;
}

export function toShiftBars(byShift: readonly ShiftStatRow[]): ShiftBar[] {
  // Heats no shift names are not a shift: they have no bar.
  return byShift.filter((s) => s.label !== NO_SHIFT_LABEL).map((s) => ({
    label: s.label,
    yieldPct: s.yieldPct,
    rejectionPct: s.rejectionPct,
  }));
}

/**
 * One bar of the grade-production chart: a grade label + parts produced.
 * `{ label, produced }`. Pure projection of `ReportResult.byGrade` (already
 * sorted desc by produced) — `[]` when empty.
 */
export interface GradeBar {
  label: string;
  produced: number;
}

export function toGradeBars(byGrade: readonly GradeStatRow[]): GradeBar[] {
  return byGrade.map((g) => ({ label: g.label, produced: g.produced }));
}

/**
 * Render a nullable percentage. **The single rule for every report surface** —
 * screen, PDF, HTML and the AI snapshot all go through here, so a bucket nobody
 * counted reads the same everywhere and cannot be re-fabricated at one call site.
 *
 * `null → "—"`, never "0.0%" (GATE-SCOPE7). The em-dash is the Slop-Gate's
 * unknown marker used across this product; `export-lot-dossier.ts` keeps its own
 * ASCII "-" because jsPDF's helvetica cannot draw an em-dash — the two are the
 * same rule with a font constraint, not two policies.
 */
export function formatPct(v: number | null, digits = 1): string {
  return v == null ? "—" : `${v.toFixed(digits)}%`;
}

/** Format a defect list as "porosity ×3, misrun ×1" (capped). Empty → "—". */
export function formatDefects(defects: DefectCount[], cap = 3): string {
  if (defects.length === 0) return "—";
  const shown = defects
    .slice(0, cap)
    .map((d) => `${d.defect.replace(/_/g, " ")} ×${d.count}`);
  const extra = defects.length - cap;
  return extra > 0 ? `${shown.join(", ")} +${extra} more` : shown.join(", ");
}

/** The words a report's unit goes by: what a row counts, and whether it has failed units to show. */
export interface ReportUnitWords {
  /** "heat" | "lot" */
  one: string;
  /** "heats" | "lots" */
  many: string;
  /** "Heats" | "Lots": a column header or a KPI label. */
  Many: string;
  /** The failed-units column's header; null for lots, which have no pass or fail of their own. */
  failed: string | null;
}

export function reportUnitWords(r: Pick<ReportResult, "unit" | "lotTotals">): ReportUnitWords {
  if (r.unit === "lot") return { one: "lot", many: "lots", Many: "Lots", failed: null };
  // A heat report of a plant that books its scrap per lot (withLotTotals): no heat fails or passes on a lot's scrap.
  return { one: "heat", many: "heats", Many: "Heats", failed: r.lotTotals ? null : "Failed heats" };
}

/**
 * The header of the shift table's count column: "Lots" where its rows count casting lots (a lot report, or a heat
 * report whose shift split is the plant's lots booked per shift: byShiftUnit), else "Heats".
 */
export function shiftCountWord(r: Pick<ReportResult, "unit" | "byShiftUnit">): "Lots" | "Heats" {
  return r.unit === "lot" || r.byShiftUnit === "lot" ? "Lots" : "Heats";
}

/**
 * Whether a report's defect figures count the scrapped castings the plant booked per defect rather than the heats that
 * recorded one: a lot report's do, and so do a heat report's whose defects are its casting lots' (withLotTotals). The
 * defect concentration's words follow it.
 */
export function defectsCountCastings(r: Pick<ReportResult, "unit" | "lotTotals">): boolean {
  return r.unit === "lot" || !!r.lotTotals;
}
